import { useLocation, useRouteMatch } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { env } from 'common/config/env'
import { useAppSelector } from 'common/hooks/redux'

import useHasCmCampaignsQuery from '../../api/queries/useHasCmCampaignsQuery'
import { PATH } from '../interfaces'

import ContextualEmptyState from './ContextualEmptyState'
import { MetabaseDashboard } from './MetabaseDashboard'
import MetabaseNavPanel from './MetabaseNavPanel'

const METRIC_LABEL_MAP: Record<string, string> = {
  num_opens: 'Num. Opens',
  num_clicks: 'Num. Clicks',
  influenced_opens: 'Num. Influenced Opens',
  direct_opens: 'Num. Direct Opens',
  total_opens: 'Num. Total Opens',
  impression: 'Num. Impression',
  click_btn1: 'Num. Click Button 1',
  click_btn2: 'Num. Click Button 2',
}

export const MetabaseCampaignOverview = () => {
  const { enableMetabaseContextual } = useFlags()
  const campaignData = useAppSelector(
    (state) => state.campaignStates?.campaignData
  )
  const location = useLocation()
  const isContextual = !!useRouteMatch(PATH.MetabaseCampaignPersonalise)
  const isContextualEnabled = isContextual && !!enableMetabaseContextual

  const optimiseDashboardId =
    process.env.REACT_APP_METABASE_CAMPAIGN_DASHBOARD_ID
  const contextualDashboardId =
    process.env.REACT_APP_METABASE_CAMPAIGN_CONTEXTUAL_DASHBOARD_ID

  const { hasCmCampaigns, isLoading } =
    useHasCmCampaignsQuery(isContextualEnabled)

  const campaignNameFromUrl = new URLSearchParams(location.search).get(
    'campaign_name'
  )
  const campaignId = isContextual
    ? (campaignNameFromUrl ?? campaignData?._id)
    : campaignData?._id

  const distribution_channel =
    campaignData?.campaign_configuration?.distribution_channel

  let metricValue =
    campaignData?.campaign_data?.bandit_data?.optimization_metric
  if (!metricValue) {
    metricValue =
      campaignData?.campaign_data?.bandit_data?.performance_metric?.split(
        '-'
      )[0]
  }

  if (distribution_channel === 'push_notification') {
    metricValue = 'direct_opens'
  } else if (
    distribution_channel === 'in_app_message' ||
    distribution_channel === 'sms' ||
    distribution_channel === 'email'
  ) {
    metricValue = 'num_opens'
  } else if (metricValue === 'clicks' || metricValue === 'opens') {
    metricValue = `num_${metricValue}`
  }
  const metric = metricValue ? METRIC_LABEL_MAP[metricValue] : undefined

  const navPanel = (
    <MetabaseNavPanel
      optimisePath={PATH.MetabaseCampaign}
      personalisePath={PATH.MetabaseCampaignPersonalise}
    />
  )

  if (!optimiseDashboardId) {
    // eslint-disable-next-line no-console
    console.error('Dashboard ID not configured')
    return (
      <div className="flex h-full items-center justify-center p-10">
        <div className="rounded bg-coolGray-50 p-6 text-base-700">
          Unable to load campaign overview. Please contact support.
        </div>
      </div>
    )
  }

  if (isContextualEnabled && !contextualDashboardId) {
    // eslint-disable-next-line no-console
    console.error('Dashboard ID not configured')
    return (
      <div className="flex h-full items-center justify-center p-10">
        <div className="rounded bg-coolGray-50 p-6 text-base-700">
          Unable to load campaign contextual overview. Please contact support.
        </div>
      </div>
    )
  }

  if (isContextualEnabled && isLoading) {
    return (
      <div className="relative overflow-y-auto overflow-x-hidden h-full flex w-full">
        {navPanel}
      </div>
    )
  }

  if (isContextualEnabled && !hasCmCampaigns) {
    return (
      <div className="relative overflow-y-auto overflow-x-hidden h-full flex w-full">
        {navPanel}
        <ContextualEmptyState />
      </div>
    )
  }

  const dashboardId = isContextual ? contextualDashboardId : optimiseDashboardId

  const additionalParams = campaignId
    ? { campaign_name: campaignId, ...(metric ? { metric } : {}) }
    : undefined

  return (
    <MetabaseDashboard
      dashboardId={dashboardId}
      additionalParams={additionalParams}
      leftNav={navPanel}
    />
  )
}
